<script setup>
import { ArrowLongLeftIcon, ArrowLongRightIcon } from "@heroicons/vue/24/solid";
import { computed } from "vue";

const props = defineProps({
  currentPage: { type: Number, required: true },
  totalPages: { type: Number, required: true },
});

const emit = defineEmits(["update:currentPage"]);

function goTo(page) {
  if (page < 1 || page > props.totalPages) return;
  emit("update:currentPage", page);
}

const pages = computed(() => {
  const total = props.totalPages;
  const current = props.currentPage;
  const output = [];

  if (total <= 8) {
    for (let i = 1; i <= total; i++) output.push(i);
    return output;
  }

  output.push(1);
  if (current >= 5) output.push("…");

  if (current < 5) {
    output.push(2, 3, 4, 5);
  } else if (current >= 5 && current < total - 4) {
    output.push(current - 1, current, current + 1);
  } else {
    output.push(total - 4, total - 3, total - 2);
  }

  if (current < total - 4) output.push("…");
  output.push(total - 1, total);

  return output;
});
</script>

<template>
  <nav class="flex justify-center border-t border-gray-700" aria-label="Pagination Navigation">
    <ul class="flex items-center text-sm font-medium" role="list">
      <!-- Previous -->
      <li>
        <button
          type="button"
          class="flex items-center px-4 pt-4 text-[#ccd1c4] hover:text-white disabled:opacity-50"
          :disabled="currentPage === 1"
          :aria-label="currentPage > 1 ? `Go to previous page, page ${currentPage - 1}` : 'Go to previous page'"
          @click="goTo(currentPage - 1)"
        >
          <ArrowLongLeftIcon class="h-5 w-5" aria-hidden="true" />
          <span v-if="currentPage > 1" class="sr-only">Previous page</span>
          <span v-else class="sr-only">Cannot go to previous page, already on first page</span>
        </button>
      </li>

      <!-- Pages -->
      <li v-for="(p, i) in pages" :key="i">
        <span
          v-if="p === '…'"
          class="flex items-center px-4 pt-4 text-[#ccd1c4]"
          aria-hidden="true"
          role="presentation"
        >
          …
        </span>
        <button
          v-else
          type="button"
          class="flex items-center px-4 pt-4 hover:text-white"
          :class="p === currentPage ? 'text-[#e05454]' : 'text-[#ccd1c4]'"
          :aria-current="p === currentPage ? 'page' : false"
          :aria-label="p === currentPage ? `Current page, page ${p}` : `Go to page ${p}`"
          @click="goTo(p)"
        >
          {{ p }}
        </button>
      </li>

      <!-- Next -->
      <li>
        <button
          type="button"
          class="flex items-center px-4 pt-4 text-[#ccd1c4] hover:text-white disabled:opacity-50"
          :disabled="currentPage === totalPages"
          :aria-label="currentPage !== totalPages ? `Go to next page, page ${currentPage + 1}` : 'Go to next page'"
          @click="goTo(currentPage + 1)"
        >
          <ArrowLongRightIcon class="h-5 w-5" aria-hidden="true" />
          <span v-if="currentPage !== totalPages" class="sr-only">Next page</span>
          <span v-else class="sr-only">Cannot go to next page, already on last page</span>
        </button>
      </li>
    </ul>
  </nav>
</template>
